import React, { useState, useEffect } from 'react';
import "@/App.css";
import { Play, Youtube, Instagram, Mail, Volume2, VolumeX } from 'lucide-react';
import SmoothScroll from './components/SmoothScroll';
import { FadeUp, FadeInSide } from './components/AnimatedSection';
import EditorialFormModal from './components/EditorialFormModal';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  HeroTypographyReveal,
  SignatureMoment,
  ChapterMarker,
  ArchitecturalRedLine,
  IdeaScanner,
  RedSignalPulse,
  ArchitecturalBlocks,
  IdeaWallCard,
  OversizedText,
  MicroGridBackground,
  EditorialMicroPhrase,
  WatermarkText,
  RedSignalSuperscript,
  ArchitecturalSpine
} from './components/EditorialExperience';

// Brand Assets
const ASSETS = {
  principalLogo: 'https://customer-assets.emergentagent.com/job_idea-signals/artifacts/dbiskgkz_logo%20principal_transparent.png',
  symbolLogo: 'https://customer-assets.emergentagent.com/job_idea-signals/artifacts/5qhxc8bz_symbol_transparent.png',
  extraSymbol: 'https://customer-assets.emergentagent.com/job_idea-signals/artifacts/168lzzh4_extra-symbol%20editorial.png',
  redAccent: 'https://customer-assets.emergentagent.com/job_idea-signals/artifacts/r9thbfsf_showyn_red_icon_transparent.png',
};

// Editorial Images
const IMAGES = {
  heroVideo: 'https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&q=80',
  heroHomeVideo: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/co11gmxb_0401%20%282%29.mp4',
  manifestoImage: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/lsx72yts_11.png',
  clariPortrait: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/ak1hdgfl_Clary.png',
  clariVideo: 'https://customer-assets-jai6qajn.emergentagent.net/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/b04m1vz3_Clary%20video%20presentation.mp4',
  magazineVideo: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/hit6r4yy_video%20magazine.mp4',
  wallCardA: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/2opua6cz_card_A_idea.png',
  wallCardB: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/o7y2dve2_card_B_bia.png',
  wallCardC: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/qxg1sj1v_card_C_lego3.png',
  wallCardD: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/79ke6upo_cardD.png',
  wallCardE: 'https://customer-assets.emergentagent.com/job_688c627c-467d-4a01-ab4d-940487d2350b/artifacts/ogvibbyp_cardE.png',
};

// TikTok Icon
const TikTokIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

// Scroll helper
const scrollToSection = (sectionId) => {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
};

// =====================================================
// VIDEO VISIBILITY HOOK — pause/mute off-screen videos
// =====================================================
const useVideoVisibility = (videoRef, { onVisible, onHidden, threshold = 0.3 } = {}) => {
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible?.(video);
        } else {
          onHidden?.(video);
        }
      },
      { threshold }
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [videoRef, onVisible, onHidden, threshold]);
};

// Lazy load hook: only loads video source when near viewport
const useLazyVideo = (ref, { threshold = 0.1 } = {}) => {
  const [shouldLoad, setShouldLoad] = React.useState(false);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShouldLoad(true); observer.disconnect(); } },
      { threshold, rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, threshold]);
  return shouldLoad;
};

// =====================================================
// NAVIGATION
// =====================================================
const Navigation = ({ onOpenForm }) => (
  <header className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6 flex justify-between items-center mix-blend-difference">
    <a href="/" data-testid="nav-logo">
      <img src={ASSETS.principalLogo} alt="SHOW'YN" className="h-8 md:h-10 w-auto invert" />
    </a>
    <button onClick={() => onOpenForm('WAITLIST')} className="font-mono text-xs uppercase tracking-[0.15em] px-6 py-3 border border-white text-white hover:bg-white hover:text-black transition-all" data-testid="nav-waitlist-btn">
      Join Waitlist
    </button>
  </header>
);

// =====================================================
// HERO SECTION - Full-bleed cinematic background video
// =====================================================
const HeroSection = ({ onOpenForm, showContent }) => {
  const heroVideoRef = React.useRef(null);
  const [heroMuted, setHeroMuted] = React.useState(true);

  const toggleHeroSound = () => {
    if (heroVideoRef.current) {
      const next = !heroMuted;
      heroVideoRef.current.muted = next;
      setHeroMuted(next);
    }
  };

  // Pause when off-screen, resume when back
  useVideoVisibility(heroVideoRef, {
    onVisible: React.useCallback((v) => { v.play().catch(() => {}); }, []),
    onHidden: React.useCallback((v) => { v.pause(); v.muted = true; setHeroMuted(true); }, []),
  });

  return (
  <section className="hero-section relative min-h-screen overflow-hidden" data-testid="hero-section">
    {/* Full-bleed background video */}
    <motion.div 
      className="hero-video-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: showContent ? 1 : 0 }}
      transition={{ duration: 2, ease: 'easeOut' }}
    >
      <video
        ref={heroVideoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        data-testid="hero-home-video"
      >
        <source src={IMAGES.heroHomeVideo} type="video/mp4" />
      </video>
      <div className="hero-overlay" />
      {/* Sound toggle - inside video container for mobile positioning */}
      <button
        onClick={toggleHeroSound}
        className="absolute bottom-8 right-8 md:right-12 z-20 p-3 border border-white/30 text-white/60 hover:text-white hover:border-white/60 transition-all duration-300 backdrop-blur-sm"
        style={{ background: 'rgba(0,0,0,0.2)' }}
        data-testid="hero-sound-btn"
      >
        {heroMuted ? <VolumeX size={16} strokeWidth={1.5} /> : <Volume2 size={16} strokeWidth={1.5} />}
      </button>
    </motion.div>

    {/* Text content layered above video */}
    <motion.div 
      className="relative z-10 h-full min-h-screen md:min-h-screen flex items-center px-6 md:px-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: showContent ? 1 : 0 }}
      transition={{ duration: 0.8, delay: 0.5 }}
    >
      <div className="max-w-7xl mx-auto w-full">
        <div className="text-left pt-24 lg:pt-0 max-w-xl lg:max-w-lg xl:max-w-xl">
          <p className="editorial-label mb-4 hero-text-shadow !text-white/70 !tracking-[0.25em]">Business Idea Architecture™</p>
          
          <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-medium mb-4 hero-text-shadow text-white">
            Where ideas become<br />
            <span className="italic">businesses</span>
            <RedSignalSuperscript size="lg" />
          </h1>
          
          <p className="font-heading text-lg md:text-xl italic mb-6 hero-text-shadow" style={{ color: 'rgba(255,255,255,0.75)' }}>
            For those in transition. Redesigning direction, life and income.
          </p>
          
          <p className="editorial-label mb-6 md:mb-12 hero-text-shadow !text-white/50">Business Ideas • AI • Strategy</p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button onClick={() => onOpenForm('TRANSITION')} className="editorial-btn bg-white text-black border-white hover:bg-editorial-red hover:text-white hover:border-editorial-red hero-btn-elevated" data-testid="hero-waitlist-btn">
              Start Where You Are
            </button>
            <button onClick={() => scrollToSection('idea-drops-section')} className="editorial-btn editorial-btn-outline !border-white !text-white hover:!bg-white hover:!text-black hero-btn-elevated" data-testid="hero-library-btn">
              Explore Idea Drops
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
        <div className="scroll-indicator">
          <span className="editorial-label text-[10px] hero-text-shadow !text-white/50">Scroll</span>
          <div className="scroll-line !bg-white/60" />
        </div>
      </div>
    </motion.div>
  </section>
  );
};

// =====================================================
// LOOK CLOSER - Before Signature Moment
// =====================================================
const LookCloserSection = () => (
  <section className="py-24 px-6 md:px-12 relative">
    <div className="max-w-7xl mx-auto text-center">
      <EditorialMicroPhrase text="You're not starting over. You're repositioning." position="center" />
    </div>
  </section>
);

// =====================================================
// CHAPTER 01: DISCOVER
// =====================================================
const Chapter01Discover = () => (
  <>
    <ChapterMarker number="01" title="DISCOVER" subtitle="Ideas are signals waiting to be decoded" />
    
    {/* IDEAS ARE SIGNALS — PRIMARY headline */}
    <section className="py-16 md:py-24 px-6 md:px-12 relative overflow-hidden" data-testid="ideas-signals-section">
      <OversizedText text="SIGNALS" />
      <MicroGridBackground opacity={0.015} />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <FadeUp>
          <div className="text-center">
            <h2 className="font-heading text-6xl md:text-8xl lg:text-9xl tracking-tight mb-8">
              IDEAS ARE
              <br />
              <span className="italic">SIGNALS</span>
              <RedSignalSuperscript size="xl" />
            </h2>
            <p className="editorial-body-lg text-neutral-600 max-w-xl mx-auto">
              They are not random thoughts.
              <br />
              They are opportunities waiting for structure.
            </p>
          </div>
        </FadeUp>
      </div>
    </section>
  </>
);

// =====================================================
// MANIFESTO SECTION — Desktop: text left + image right. Mobile: image + text stacked
// =====================================================
const ManifestoSection = () => (
  <section className="py-10 md:py-20 px-6 md:px-12 relative overflow-hidden" data-testid="manifesto-section">
    <div className="max-w-6xl mx-auto relative z-10">
      {/* Desktop: 2-col split layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* LEFT: Text */}
        <FadeInSide direction="left">
          <div className="order-2 lg:order-1">
            <p className="font-heading text-2xl md:text-3xl italic text-neutral-800 leading-relaxed mb-6">
              Some ideas are not businesses.<br />
              They are turning points.
            </p>
            <p className="editorial-body-lg text-neutral-500 leading-relaxed">
              Every signal carries a frequency. When decoded, it reveals the architecture of opportunity.
              At SHOW'YN, we don't follow what's popular. We identify what's inevitable.
            </p>
          </div>
        </FadeInSide>

        {/* RIGHT: Image (contains "We don't chase trends" visually) */}
        <FadeInSide direction="right" delay={0.15}>
          <motion.div 
            className="relative order-1 lg:order-2"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.4 }}
          >
            <img 
              src={IMAGES.manifestoImage}
              alt="SHOW'YN editorial visual — We Don't Chase Trends, We Design Businesses"
              className="w-full h-auto"
              loading="lazy"
            />
          </motion.div>
        </FadeInSide>
      </div>
    </div>
  </section>
);

// =====================================================
// CHAPTER 02: STRUCTURE
// =====================================================
const Chapter02Structure = () => (
  <>
    <ChapterMarker number="02" title="STRUCTURE" subtitle="Transform uncertainty into direction" />
    
    {/* Micro-phrase */}
    <div className="py-12 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <EditorialMicroPhrase text="What feels like confusion is often unstructured potential." position="right" />
      </div>
    </div>
    
    {/* What is BIA */}
    <section className="section-spacing px-6 md:px-12 bg-editorial-muted relative overflow-hidden" data-testid="what-is-bia-section">
      <OversizedText text="ARCHITECTURE" />
      <MicroGridBackground opacity={0.025} />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <FadeUp>
          <h2 className="editorial-heading-md mb-8 text-center">
            What is Business Idea Architecture?
          </h2>
        </FadeUp>
        
        <FadeUp delay={0.2}>
          <div className="max-w-3xl mx-auto text-center">
            <p className="editorial-body-lg text-neutral-700 mb-8">
              Business Idea Architecture is the method created by SHOW'YN to transform 
              raw concepts into viable, profitable ventures.
            </p>
            <p className="editorial-body-lg text-neutral-700 mb-10">
              It focuses on discovering opportunities, validating signals, structuring 
              the business model and defining a clear path to execution.
            </p>
            
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2">
              <span className="font-heading text-lg">• new business ideas</span>
              <span className="font-heading text-lg">• career transitions</span>
              <span className="font-heading text-lg">• brand expansions</span>
              <span className="font-heading text-lg">• life direction</span>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>

    {/* The BIA Method with Architectural Spine */}
    <section className="section-spacing-lg px-6 md:px-12 relative overflow-hidden" data-testid="architecture-section">
      <OversizedText text="METHOD" />
      <MicroGridBackground opacity={0.03} />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <FadeUp>
          <div className="text-center mb-8">
            <h2 className="editorial-heading-md mb-6">The BIA Method</h2>
            <img src={ASSETS.extraSymbol} alt="SHOW'YN architectural symbol — Business Idea Architecture method" className="h-12 w-auto mx-auto mb-6" loading="lazy" data-testid="architecture-symbol" />
            {/* NEW: Method intro for transition positioning */}
            <p className="font-heading text-lg md:text-xl italic text-neutral-600 max-w-xl mx-auto mb-4">
              This is not just a process for building businesses.
              <br />
              It's a structure for navigating moments of transition.
            </p>
            <p className="editorial-label">Humanized Marketing • Intelligent Automation</p>
          </div>
        </FadeUp>

        {/* Architectural Spine Layout */}
        <ArchitecturalSpine />
      </div>
    </section>
  </>
);

// Hover config for Idea Wall items
const wallHover = {
  scale: 1.03,
  y: -5,
  boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.15)',
};
const wallTransition = { duration: 0.3, ease: 'easeOut' };

// =====================================================
// SHOW'YN MAGAZINE - Editorial Video Section
// =====================================================
const ShowynMagazineSection = () => {
  const magVideoRef = React.useRef(null);
  const magContainerRef = React.useRef(null);
  const shouldLoadMag = useLazyVideo(magContainerRef);

  useVideoVisibility(magVideoRef, {
    onVisible: React.useCallback((v) => { v.play().catch(() => {}); }, []),
    onHidden: React.useCallback((v) => { v.pause(); }, []),
  });

  return (
  <section className="py-20 md:py-28 px-6 md:px-12 overflow-hidden relative" data-testid="gallery-section">
    <MicroGridBackground opacity={0.025} />
    
    <div className="absolute top-8 right-8 md:right-12 z-10">
      <EditorialMicroPhrase text="Ideas are happening here." position="right" />
    </div>
    
    <div className="max-w-7xl mx-auto relative">
      <div className="flex flex-col md:flex-row">
        {/* Vertical Title */}
        <FadeUp>
          <div className="hidden md:flex items-start justify-center w-14 mr-4 pt-2">
            <h2 
              className="font-heading text-4xl tracking-[0.3em] text-neutral-200 whitespace-nowrap"
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', transform: 'rotate(180deg)' }}
            >
              SHOW'YN MAGAZINE
            </h2>
          </div>
        </FadeUp>

        <div className="md:hidden mb-6">
          <FadeUp>
            <h2 className="font-heading text-3xl tracking-[0.15em] text-neutral-200">SHOW'YN MAGAZINE</h2>
          </FadeUp>
        </div>

        {/* Video filling the right side */}
        <div className="flex-1" ref={magContainerRef}>
          <FadeUp>
            <div className="relative overflow-hidden" data-testid="magazine-video-container">
              {shouldLoadMag ? (
                <video
                  ref={magVideoRef}
                  autoPlay muted loop playsInline preload="auto"
                  className="w-full h-auto block"
                  data-testid="magazine-video"
                >
                  <source src={IMAGES.magazineVideo} type="video/mp4" />
                </video>
              ) : (
                <div className="w-full bg-neutral-100" style={{ aspectRatio: '16/9' }} />
              )}
              {/* Text overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.6))' }}>
                <p className="font-heading text-xl md:text-3xl lg:text-4xl text-white leading-relaxed tracking-wide" style={{ fontWeight: 300 }}>
                  Both can fail.<br />
                  Only one refines<span className="text-editorial-red">.</span>
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  </section>
  );
};

// =====================================================
// PERSONAS SECTION
// =====================================================
const PersonasSection = ({ onOpenForm }) => (
  <section className="section-spacing-lg px-6 md:px-12 relative overflow-hidden" data-testid="personas-section">
    <OversizedText text="WHO" />
    
    <div className="max-w-7xl mx-auto relative z-10">
      <FadeUp>
        <h2 className="editorial-heading-md mb-16 text-center">Who is SHOW'YN for?</h2>
      </FadeUp>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        <FadeUp delay={0.1}>
          <div className="border border-editorial-border p-8 md:p-10 h-full flex flex-col hover:border-editorial-red transition-colors duration-300">
            <span className="editorial-label text-editorial-red mb-6">Persona 01</span>
            <h3 className="font-heading text-2xl md:text-3xl mb-6 italic">You already have an idea.</h3>
            <p className="editorial-body-lg text-neutral-600 mb-8 flex-grow">
              But you need clarity, focus and structure to turn it into something real.
            </p>
            <button onClick={() => onOpenForm('TRANSITION')} className="editorial-btn self-start text-sm" data-testid="persona1-cta-btn">
              Start Where You Are
            </button>
          </div>
        </FadeUp>

        <FadeUp delay={0.2}>
          <div className="border border-editorial-border p-8 md:p-10 h-full flex flex-col hover:border-editorial-red transition-colors duration-300">
            <span className="editorial-label text-editorial-red mb-6">Persona 02</span>
            <h3 className="font-heading text-2xl md:text-3xl mb-6 italic">You don't have the idea yet.</h3>
            <p className="editorial-body-lg text-neutral-600 mb-8 flex-grow">
              But you feel the need to build something meaningful and aligned with your life.
            </p>
            <button onClick={() => onOpenForm('DISCOVERY')} className="editorial-btn editorial-btn-outline self-start text-sm" data-testid="persona2-cta-btn">
              Understand Your Next Move
            </button>
          </div>
        </FadeUp>

        <FadeUp delay={0.3}>
          <div className="border border-editorial-border p-8 md:p-10 h-full flex flex-col hover:border-editorial-red transition-colors duration-300">
            <span className="editorial-label text-editorial-red mb-6">Persona 03</span>
            <h3 className="font-heading text-2xl md:text-3xl mb-6 italic">You feel lost in direction.</h3>
            <p className="editorial-body-lg text-neutral-600 mb-8 flex-grow">
              You know something needs to change, but you don't know what or how.
              <br /><br />
              You are not starting over. You are repositioning.
            </p>
            <button onClick={() => onOpenForm('IDEA_READY')} className="editorial-btn self-start text-sm" data-testid="persona3-cta-btn">
              Turn Your Current Moment Into Direction
            </button>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

// =====================================================
// CHAPTER 03: BUILD
// =====================================================
const Chapter03Build = () => (
  <>
    <ChapterMarker number="03" title="BUILD" subtitle="Execute and launch with clarity" />
    
    {/* Editorial micro-line */}
    <div className="py-12 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <EditorialMicroPhrase text="Not every idea is about business. Some are about direction." position="left" />
      </div>
    </div>
  </>
);

// =====================================================
// CLARY SECTION
// =====================================================
const ClarySection = () => {
  const claryVideoRef = React.useRef(null);
  const claryContainerRef = React.useRef(null);
  const [claryMuted, setClaryMuted] = React.useState(true);

  const toggleClarySound = () => {
    if (claryVideoRef.current) {
      const next = !claryMuted;
      claryVideoRef.current.muted = next;
      setClaryMuted(next);
    }
  };

  const shouldLoadClary = useLazyVideo(claryContainerRef);

  // Pause + mute when off-screen
  useVideoVisibility(claryVideoRef, {
    onVisible: React.useCallback((v) => { v.play().catch(() => {}); }, []),
    onHidden: React.useCallback((v) => { v.pause(); v.muted = true; setClaryMuted(true); }, []),
  });

  return (
  <section className="py-24 md:py-32 px-6 md:px-12 bg-black text-white relative overflow-hidden" data-testid="clari-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left: Text introduction */}
        <div>
          <FadeInSide direction="left">
            <div>
              <span className="editorial-label text-neutral-400 mb-6 block">Clary, AI Business Architect™</span>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-8" data-testid="clary-title">
                Clary, AI Business Architect
                <RedSignalSuperscript size="lg" className="brightness-0 invert" />
              </h2>
              <p className="editorial-body-lg text-neutral-300 mb-10 leading-relaxed">
                Clary is your AI Business Architect, designed to decode your signals,
                identify opportunities and structure your next move.
              </p>
              <div className="border-l-2 border-editorial-red pl-6">
                <p className="font-heading text-2xl italic text-neutral-200">
                  "That's how your brand grows to show."
                </p>
              </div>
            </div>
          </FadeInSide>
        </div>

        {/* Right: New 9:16 Clary video */}
        <div ref={claryContainerRef}>
          <FadeInSide direction="right" delay={0.2} className="flex justify-center lg:justify-end">
            <div
              className="relative"
              style={{ width: '360px', maxWidth: '100%', aspectRatio: '9 / 16' }}
              data-testid="clary-video-wrapper"
            >
              {shouldLoadClary ? (
                <video
                  ref={claryVideoRef}
                  autoPlay muted loop playsInline preload="auto"
                  className="w-full h-full object-contain"
                  style={{ aspectRatio: '9 / 16' }}
                  data-testid="clary-video"
                >
                  <source src={IMAGES.clariVideo} type="video/mp4" />
                </video>
              ) : (
                <div className="w-full h-full bg-neutral-900" style={{ aspectRatio: '9 / 16' }} />
              )}
              {/* Sound toggle */}
              <button
                onClick={toggleClarySound}
                className="absolute bottom-4 right-4 z-20 p-2.5 border border-white/30 text-white/60 hover:text-white hover:border-white/60 transition-all duration-300"
                style={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }}
                data-testid="clary-sound-btn"
              >
                {claryMuted ? <VolumeX size={16} strokeWidth={1.5} /> : <Volume2 size={16} strokeWidth={1.5} />}
              </button>
            </div>
          </FadeInSide>
        </div>
      </div>
    </div>
  </section>
  );
};

// =====================================================
// ECOSYSTEM SECTION
// =====================================================
const EcosystemSection = ({ onOpenForm }) => {
  const tiers = [
    { level: 'Free', name: 'Idea Drops', description: 'Weekly insights on business signals and emerging opportunities, designed to create clarity and direction.', cta: 'Follow the Idea Drops', action: () => scrollToSection('idea-drops-section') },
    { level: 'Paid', name: 'The Library', description: 'Frameworks, tools and resources to structure your next move, turning ideas into clear direction.', cta: 'Join the Library Waitlist', action: () => onOpenForm('WAITLIST') },
    { level: 'Premium', name: 'Strategy & Guidance', description: 'Direct collaboration with our team to architect your path forward.', cta: 'Request Guidance', action: () => onOpenForm('PREMIUM') },
  ];

  return (
    <section className="section-spacing-lg px-6 md:px-12 relative overflow-hidden" data-testid="ecosystem-section">
      <OversizedText text="ECOSYSTEM" />
      <MicroGridBackground opacity={0.015} />
      
      <div className="max-w-5xl mx-auto relative z-10">
        <FadeUp>
          <div className="text-center mb-12">
            <h2 className="editorial-heading-md mb-4">The SHOW'YN Ecosystem</h2>
            <p className="editorial-label mb-10">Business Ideas • AI • Strategy</p>
            <p className="font-heading text-xl md:text-2xl italic text-neutral-600 max-w-2xl mx-auto leading-relaxed">
              Whether you're building something new,<br />
              or redefining your current path,<br />
              this is where clarity becomes direction.
            </p>
          </div>
        </FadeUp>

        {tiers.map((tier, index) => (
          <FadeUp key={tier.level} delay={index * 0.15}>
            <div className="border-t border-editorial-border py-10 grid grid-cols-12 gap-6 items-start">
              <div className="col-span-12 md:col-span-2">
                <span className="editorial-label text-editorial-red">{tier.level}</span>
              </div>
              <div className="col-span-12 md:col-span-3">
                <h3 className="font-heading text-3xl italic">{tier.name}</h3>
              </div>
              <div className="col-span-12 md:col-span-5">
                <p className="editorial-body-lg text-neutral-600 mb-4">{tier.description}</p>
                <button onClick={tier.action} className="font-mono text-xs uppercase tracking-widest text-black hover:text-editorial-red transition-colors underline underline-offset-4" data-testid={`ecosystem-${tier.level.toLowerCase()}-cta`}>
                  {tier.cta} →
                </button>
              </div>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>
  );
};

// =====================================================
// IDEA DROPS SECTION + BUSINESS LIBRARY
// =====================================================
const LIBRARY_CATEGORIES = [
  {
    category: 'REDESIGN BUSINESS',
    cases: [
      {
        id: 'RB-001',
        caseLabel: 'CASE 001',
        displayName: 'Redesign Business Case 001',
        trackingName: 'Redesign Business — Case 001',
        title: 'What if AI redesigned the way we give gifts?',
        url: 'https://drive.google.com/file/d/1tx-Huue1xH4n61dwCIK5rxW-bFNvTmiO/view?usp=sharing',
      },
    ],
  },
  {
    category: 'BUSINESS ARCHITECTURE IDEA',
    cases: [
      {
        id: 'BAI-001',
        caseLabel: 'CASE 001',
        displayName: 'Case 001 — VENYOU',
        trackingName: 'Business Architecture Idea — VENYOU',
        url: 'https://drive.google.com/file/d/1zq_1WY3SIlvTApgK6EowSZJBAjD1OHcO/view?usp=drive_link',
      },
      {
        id: 'BAI-002',
        caseLabel: 'CASE 002',
        displayName: 'Case 002 — CLEANER WORKFORCE',
        trackingName: 'Business Architecture Idea — CLEANER WORKFORCE',
        url: 'https://drive.google.com/file/d/14Padk-BpUwQQXyyEIGdJeWKWHuszsloH/view?usp=drive_link',
      },
    ],
  },
];

const IdeaDropsSection = () => {
  const [libraryState, setLibraryState] = React.useState('closed'); // closed | form | library
  const [selectedCase, setSelectedCase] = React.useState(null);
  const [formData, setFormData] = React.useState({ name: '', email: '', redesign_next: '' });
  const [formError, setFormError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleCaseClick = (caseItem) => {
    setSelectedCase(caseItem);
    setFormError('');
    setLibraryState('form');
  };

  const handleLibrarySubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setFormError('Name and email are required.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      const res = await fetch(`${API}/forms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'LIBRARY',
          name: formData.name,
          email: formData.email,
          responses: {
            redesign_next: formData.redesign_next,
            case_id: selectedCase?.id || '',
            case_name: selectedCase?.trackingName || '',
          },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        const detail = err?.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Registration failed');
      }
      const data = await res.json();
      if (data.convertkit_synced) {
        if (selectedCase?.url) {
          window.open(selectedCase.url, '_blank', 'noopener,noreferrer');
        }
        setLibraryState('library');
      } else {
        setFormError('Registration could not be completed. Please try again.');
      }
    } catch (e) {
      setFormError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
  <section id="idea-drops-section" className="section-spacing-lg px-6 md:px-12 bg-editorial-muted relative" data-testid="idea-drops-section">
    <MicroGridBackground opacity={0.02} />
    
    <div className="max-w-4xl mx-auto relative z-10">
      {/* Existing Idea Drops content */}
      <FadeUp>
        <div className="text-center">
          <span className="editorial-label text-editorial-red mb-4 block">Weekly Content</span>
          <h2 className="editorial-heading-md mb-4">Idea Drops</h2>
          <p className="editorial-label mb-8">Weekly business ideas, signals and opportunities.</p>
        </div>
      </FadeUp>

      <FadeUp delay={0.2}>
        <p className="editorial-body-lg text-neutral-600 mb-12 max-w-2xl mx-auto text-center">
          Every week we share fresh insights, signals and opportunities across our channels.
          Follow SHOW'YN to discover what's next before anyone else.
        </p>
      </FadeUp>

      <FadeUp delay={0.3}>
        <div className="flex justify-center items-center gap-8 mb-8">
          {[
            { icon: Youtube, href: 'https://www.youtube.com/@Showyn_brand', testId: 'social-youtube', external: true },
            { icon: Instagram, href: 'https://www.instagram.com/showyn.brand/', testId: 'social-instagram', external: true },
            { icon: Mail, href: 'mailto:start@showyn.io', testId: 'social-email', external: false },
          ].map(({ icon: Icon, href, testId, external }) => (
            <a key={testId} href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className="p-4 border border-editorial-border hover:border-editorial-red hover:text-editorial-red transition-all" data-testid={testId}>
              <Icon size={28} strokeWidth={1.5} />
            </a>
          ))}
        </div>
        <p className="font-heading text-lg italic text-neutral-500 text-center">
          Follow SHOW'YN to discover new signals and opportunities every week.
        </p>
      </FadeUp>

      {/* ---- FREE BUSINESS RESOURCES ---- */}
      <div id="business-library" className="border-t border-neutral-300/50 mt-16 pt-16">
        <FadeUp delay={0.1}>
          <div className="text-center mb-8">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-editorial-red mb-4 block">Free Business Resources</span>
            <p className="font-heading text-2xl md:text-3xl italic text-neutral-800 leading-relaxed">
              Ideas are only the beginning.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.2}>
          <p className="editorial-body-lg text-neutral-500 text-center max-w-xl mx-auto mb-10">
            Explore the thinking, research and business architecture behind selected SHOW'YN projects.
          </p>
        </FadeUp>

        {/* STATE: Closed — show categories with CTAs */}
        {libraryState === 'closed' && (
          <FadeUp delay={0.3}>
            <div className="space-y-12" data-testid="library-cases-list">
              {LIBRARY_CATEGORIES.map((cat, ci) => (
                <div key={cat.category}>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-editorial-red mb-6 text-center">{cat.category}</p>
                  <div className="space-y-8">
                    {cat.cases.map((c) => (
                      <div key={c.id} className="text-center" data-testid={`library-cta-${c.id}`}>
                        <p className="font-heading text-base text-neutral-600 mb-3">
                          Download <span className="italic">{c.displayName}</span> for Free.
                        </p>
                        {c.title && (
                          <p className="font-heading text-sm italic text-neutral-400 mb-4">{c.title}</p>
                        )}
                        <button
                          onClick={() => handleCaseClick(c)}
                          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] px-8 py-3.5 border border-editorial-red text-editorial-red hover:bg-editorial-red hover:text-white transition-all duration-300"
                          data-testid={`library-cta-btn-${c.id}`}
                        >
                          Get the free case study <span>→</span>
                        </button>
                      </div>
                    ))}
                  </div>
                  {ci < LIBRARY_CATEGORIES.length - 1 && (
                    <div className="border-b border-neutral-300/30 mt-12" />
                  )}
                </div>
              ))}
            </div>
          </FadeUp>
        )}

        {/* STATE: Form — inline elegant form */}
        {libraryState === 'form' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-md mx-auto"
          >
            <div className="border-t border-b border-neutral-300/50 py-10">
              <h3 className="font-heading text-xl md:text-2xl text-center mb-2">Access the SHOW'YN Business Library</h3>
              <p className="editorial-body-lg text-neutral-500 text-center mb-8 text-sm">
                Get free access to selected business studies, frameworks and redesign cases created by SHOW'YN.
              </p>

              {selectedCase && (
                <p className="font-mono text-[10px] tracking-[0.2em] text-editorial-red text-center mb-6 uppercase" data-testid="library-selected-case">
                  Requesting: {selectedCase.displayName}
                </p>
              )}

              <div className="space-y-6">
                <div>
                  <label className="editorial-label mb-2 block">First Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Your first name"
                    className="editorial-input"
                    data-testid="library-name-input"
                  />
                </div>
                <div>
                  <label className="editorial-label mb-2 block">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    placeholder="your@email.com"
                    className="editorial-input"
                    data-testid="library-email-input"
                  />
                </div>
                <div>
                  <label className="editorial-label mb-2 block">What type of business would you like SHOW'YN to redesign / create next?</label>
                  <textarea
                    value={formData.redesign_next}
                    onChange={e => setFormData(p => ({ ...p, redesign_next: e.target.value }))}
                    placeholder="E.g. fitness studios, local bakeries, personal finance..."
                    className="editorial-input min-h-[70px] resize-none"
                    data-testid="library-redesign-input"
                  />
                </div>
              </div>

              {formError && <p className="text-editorial-red text-sm font-mono mt-4">{formError}</p>}

              <div className="mt-8 text-center">
                <button
                  onClick={handleLibrarySubmit}
                  disabled={submitting}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-black hover:text-editorial-red transition-colors disabled:opacity-40 inline-flex items-center gap-2"
                  data-testid="library-submit-btn"
                >
                  {submitting ? 'Submitting...' : <>Access the library <span className="text-editorial-red">→</span></>}
                </button>
              </div>

              <div className="mt-4 text-center">
                <button
                  onClick={() => { setLibraryState('closed'); setSelectedCase(null); }}
                  className="font-mono text-[10px] tracking-[0.15em] text-neutral-400 hover:text-neutral-600 transition-colors uppercase"
                  data-testid="library-back-btn"
                >
                  ← Back
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* STATE: Library — show resources by category */}
        {libraryState === 'library' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="business-library"
          >
            <div className="text-center mb-12">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-editorial-red mb-3 block">SHOW'YN Business Library</span>
              <p className="font-heading text-xl italic text-neutral-700">Your access is ready.</p>
            </div>

            <div className="space-y-10">
              {LIBRARY_CATEGORIES.map((cat) => (
                <div key={cat.category}>
                  <p className="font-mono text-xs uppercase tracking-[0.2em] text-editorial-red mb-4">{cat.category}</p>
                  <div className="space-y-0">
                    {cat.cases.map((c, ci) => (
                      <div
                        key={c.id}
                        className={`border-t border-neutral-300/50 py-8 ${ci === cat.cases.length - 1 ? 'border-b' : ''}`}
                        data-testid={`library-resource-${c.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-mono text-[10px] tracking-[0.25em] text-neutral-400">{c.caseLabel}</span>
                              <span className="font-mono text-[10px] tracking-[0.15em] text-editorial-red">AVAILABLE</span>
                            </div>
                            <p className="editorial-label text-black mb-1">{c.displayName}</p>
                            {c.title && (
                              <p className="font-heading text-lg md:text-xl text-neutral-800 italic">{c.title}</p>
                            )}
                          </div>
                          <div className="md:text-right md:flex-shrink-0 md:pt-1">
                            <a
                              href={c.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-mono text-xs uppercase tracking-[0.2em] text-black hover:text-editorial-red transition-colors inline-flex items-center gap-2"
                              data-testid={`library-download-${c.id}`}
                            >
                              Download {c.caseLabel} <span className="text-editorial-red">→</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  </section>
  );
};

// =====================================================
// GUIDANCE SECTION
// =====================================================
const GuidanceSection = ({ onOpenForm }) => (
  <section id="guidance-section" className="section-spacing-lg px-6 md:px-12 relative overflow-hidden" data-testid="guidance-section">
    <OversizedText text="GUIDANCE" />
    
    {/* Editorial micro-line */}
    <div className="absolute top-8 right-8 md:right-12 z-10">
      <EditorialMicroPhrase text="Clarity turns uncertainty into movement." position="right" />
    </div>
    
    <div className="max-w-3xl mx-auto text-center relative z-10">
      <FadeUp>
        <h2 className="editorial-heading-md mb-6">Need guidance?</h2>
      </FadeUp>
      <FadeUp delay={0.2}>
        <p className="editorial-body-lg text-neutral-600 mb-12">
          If you have a concept, a product, or the beginning of something
          and need help structuring what comes next, the SHOW'YN team is here.
        </p>
      </FadeUp>
      <FadeUp delay={0.3}>
        <button onClick={() => onOpenForm('PREMIUM')} className="editorial-btn" data-testid="guidance-idea-btn">
          Understand Your Next Move
        </button>
      </FadeUp>
    </div>
  </section>
);

// =====================================================
// LARGE STATEMENT
// =====================================================
const LargeStatement = () => (
  <section className="section-spacing-lg px-6 md:px-12 relative" data-testid="statement-section">
    <MicroGridBackground opacity={0.02} />
    
    <div className="max-w-6xl mx-auto text-center relative z-10">
      <FadeUp>
        <div className="space-y-4">
          <span className="font-heading text-6xl md:text-8xl lg:text-9xl block">Humanized</span>
          <span className="font-heading text-6xl md:text-8xl lg:text-9xl block italic">Marketing</span>
          <span className="font-heading text-6xl md:text-8xl lg:text-9xl block mt-8">Intelligent</span>
          <span className="font-heading text-6xl md:text-8xl lg:text-9xl block italic">Automation</span>
        </div>
      </FadeUp>
      <FadeUp delay={0.4}>
        <div className="mt-12">
          <RedSignalPulse size="xl" />
        </div>
      </FadeUp>
    </div>
  </section>
);

// =====================================================
// FINAL CTA
// =====================================================
// =====================================================
// IN TRANSITION?
// =====================================================
const InTransitionSection = () => (
  <section className="py-32 md:py-40 px-6 md:px-12 relative" data-testid="in-transition-section">
    <div className="max-w-2xl mx-auto text-center">
      <FadeUp>
        <span className="editorial-label text-editorial-red mb-6 block">In transition?</span>
      </FadeUp>
      <FadeUp delay={0.2}>
        <p className="font-heading text-xl md:text-2xl leading-relaxed text-neutral-600" style={{ lineHeight: '2.2' }}>
          Not everyone comes here with a business idea.<br />
          Some come with uncertainty.<br />
          Some with the feeling that something needs to change.
        </p>
      </FadeUp>
      <FadeUp delay={0.4}>
        <p className="font-heading text-2xl md:text-3xl italic text-neutral-800 mt-10">
          That's where we begin.
        </p>
      </FadeUp>
    </div>
  </section>
);

const FinalCTA = ({ onOpenForm }) => (
  <section id="waitlist-section" className="section-spacing-lg px-6 md:px-12 bg-editorial-muted relative" data-testid="final-cta-section">
    <MicroGridBackground opacity={0.02} />
    
    <div className="max-w-3xl mx-auto text-center relative z-10">
      <FadeUp>
        <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-8">
          Ready to turn ideas<br />into <span className="italic">architecture</span>?
          <RedSignalPulse size="md" className="ml-2" />
        </h2>
      </FadeUp>
      <FadeUp delay={0.2}>
        <button onClick={() => onOpenForm('IDEA_SUBMISSION')} className="editorial-btn" data-testid="final-waitlist-btn">
          Turn Your Current Moment Into Direction
        </button>
      </FadeUp>
    </div>
  </section>
);

// =====================================================
// ENTRY POINT - LP Copy
// =====================================================
const EntryPointSection = () => (
  <div className="pb-12 md:pb-16 px-6 md:px-12" data-testid="entry-point-section">
    <div className="max-w-2xl mx-auto text-center">
      <FadeUp>
        <p className="font-heading text-xl md:text-2xl italic text-neutral-700 leading-relaxed">
          Each form is not a form.<br />
          It's an entry point into the system.
        </p>
      </FadeUp>
      <FadeUp delay={0.2}>
        <p className="editorial-body-lg text-neutral-400 mt-6 leading-relaxed">
          Join or submit your answers to receive a curated overview,
          including ideas, direction, and a personalised 2026 pathway guide.
        </p>
      </FadeUp>
    </div>
  </div>
);

// =====================================================
// FOOTER
// =====================================================
const Footer = () => (
  <footer className="footer-section px-6 md:px-12" data-testid="footer">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-12">
        <div>
          <img src={ASSETS.principalLogo} alt="SHOW'YN — Where Ideas Become Businesses" className="h-10 w-auto" loading="lazy" data-testid="footer-logo" />
        </div>
        <div className="md:text-right">
          <img src={ASSETS.symbolLogo} alt="SHOW'YN brand symbol" className="h-12 w-auto md:ml-auto" loading="lazy" data-testid="footer-symbol" />
        </div>
      </div>

      <div className="arch-line-horizontal w-full mb-8" />

      <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-8">
        <p className="font-heading text-lg italic text-neutral-600">"That's how your brand grows to show."</p>
        <p className="editorial-label text-neutral-400">© {new Date().getFullYear()} SHOW'YN</p>
      </div>

      <div className="text-center pt-6 border-t border-editorial-border">
        <p className="text-xs text-neutral-400 font-mono tracking-wide">
          Business Idea Architecture™ is a proprietary methodology by SHOW'YN.
        </p>
      </div>
    </div>
  </footer>
);

// =====================================================
// MAIN APP
// =====================================================
function App() {
  const [activeForm, setActiveForm] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const skipIntro = sessionStorage.getItem('skipIntro');
    if (skipIntro) {
      setShowIntro(false);
      setShowContent(true);
    }
  }, []);

  // Handle hash anchor scrolling (Lenis smooth scroll blocks native hash)
  useEffect(() => {
    if (showContent && window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, [showContent]);

  const handleIntroComplete = () => {
    setShowIntro(false);
    setShowContent(true);
    sessionStorage.setItem('skipIntro', 'true');
  };

  return (
    <SmoothScroll>
      <div className="App bg-white min-h-screen" data-testid="app">
        {showIntro && <HeroTypographyReveal onComplete={handleIntroComplete} />}
        <ArchitecturalRedLine />
        <IdeaScanner />

        <Navigation onOpenForm={setActiveForm} />

        <main>
          <HeroSection onOpenForm={setActiveForm} showContent={showContent} />
          <LookCloserSection />
          <SignatureMoment />
          <Chapter01Discover />
          <ManifestoSection />
          <Chapter02Structure />
          <ShowynMagazineSection />
          <PersonasSection onOpenForm={setActiveForm} />
          <EntryPointSection />
          <Chapter03Build />
          <ClarySection />
          <EcosystemSection onOpenForm={setActiveForm} />
          <IdeaDropsSection />
          <GuidanceSection onOpenForm={setActiveForm} />
          <LargeStatement />
          <InTransitionSection />
          <FinalCTA onOpenForm={setActiveForm} />
        </main>

        <Footer />

        <EditorialFormModal
          formType={activeForm}
          isOpen={!!activeForm}
          onClose={() => setActiveForm(null)}
        />
      </div>
    </SmoothScroll>
  );
}

export default App;
